import { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useBusinesses } from "@/hooks/use-businesses";
import { getNotifications, markAsRead, markAllAsRead, type Notification } from "@/services/notificationService";
import { Bell, AlertTriangle, CheckCircle, BarChart3, Brain, Clock, X } from "lucide-react";

const ICONS: Record<string, any> = {
  alert: AlertTriangle,
  check: CheckCircle,
  chart: BarChart3,
  brain: Brain,
  clock: Clock,
};

const TYPE_COLORS: Record<string, string> = {
  warning: "border-l-yellow-500 bg-yellow-50",
  success: "border-l-green-500 bg-green-50",
  action: "border-l-blue-500 bg-blue-50",
  info: "border-l-gray-500 bg-gray-50",
};

export function NotificationCenter() {
  const { activeBusiness } = useBusinesses();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const businessId = activeBusiness?.id;

  const { data: notifications = [] } = useQuery<Notification[]>({
    queryKey: ["/notifications", businessId],
    queryFn: () => businessId ? getNotifications(businessId) : Promise.resolve([]),
    enabled: !!businessId,
    refetchInterval: 15000,
    staleTime: 10000,
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleClick = useCallback((n: Notification) => {
    markAsRead(n.id);
    queryClient.invalidateQueries({ queryKey: ["/notifications"] });
    if (n.actionUrl) navigate(n.actionUrl);
    setOpen(false);
  }, [navigate, queryClient]);

  const handleMarkAllRead = useCallback(() => {
    markAllAsRead(notifications);
    queryClient.invalidateQueries({ queryKey: ["/notifications"] });
  }, [notifications, queryClient]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-notification-center]")) setOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [open]);

  const timeAgo = (ts: string) => {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="relative" data-notification-center>
      <Button variant="ghost" size="icon" className="relative" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 md:w-96 bg-white rounded-lg shadow-xl border z-50 max-h-[70vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="font-semibold text-sm">Notifications</div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button className="text-xs text-blue-600 hover:underline" onClick={handleMarkAllRead}>
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)}><X className="h-4 w-4 text-muted-foreground" /></button>
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = ICONS[n.icon] || Bell;
                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-l-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      TYPE_COLORS[n.type] || TYPE_COLORS.info
                    } ${n.read ? "opacity-60" : ""}`}
                    onClick={() => handleClick(n)}
                  >
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${
                      n.type === "warning" ? "text-yellow-600" :
                      n.type === "success" ? "text-green-600" :
                      n.type === "action" ? "text-blue-600" : "text-gray-500"
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium leading-tight">{n.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.timestamp)}</div>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
